import { useCallback, useEffect, useState, forwardRef, useImperativeHandle } from 'react';

import { api } from '../api.js';
import { toast } from './Toasts.jsx';

// T406 — links worth a second look.
//
// Three times this week he reported a message filed on the wrong
// application, and each time he found it before the platform did: a Redpill
// Linpro confirmation on a SYBO row, an IFS one on Too Good To Go, a CBS one
// on a DevOps role from a month earlier. The rules behind those are fixed,
// but the pattern is the point — a wrong link stays invisible until he
// happens to open that application.
//
// Two signals, one list, because the action is the same. The company on the
// row is named nowhere in the message, so something inferred it; or (T480)
// the subject spells out a DIFFERENT role at that company, which is the case
// the company check cannot see — an Alm. Brand mail naming "Platform Engineer
// – IaC, Azure" sat on their Data Platform Engineer row with the company
// corroborated by the sender's own ATS domain.
//
// Neither is proof of error — Satair never appears in an Airbus mail — which
// is why this lists rather than acts, and why unlinking is a button rather
// than something it does on its own.

const SuspiciousLinks = forwardRef(function SuspiciousLinks({ onDone, onCount }, ref) {
  const [items, setItems] = useState(null);
  // T434 — invisible when there is nothing is right; invisible when the
  // check could not run says "nothing" and means "I do not know".
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/emails/suspicious');
      setItems(r.items ?? []);
      setFailed(false);
      // T414 — the page's "nothing waiting" line counted four kinds of work
      // and not this one, so it could print the all-clear directly under a
      // suspect link it was listing.
      onCount?.(r.items?.length ?? 0);
    } catch {
      setFailed(true);
      setItems([]);
      onCount?.(0);
    }
  }, [onCount]);

  useImperativeHandle(ref, () => ({ reload: load }), [load]);
  useEffect(() => {
    load();
  }, [load]);

  const unlink = async (it) => {
    setBusy(it.emailId);
    try {
      // The ordinary route, which records the change in history so it can be
      // undone (T394) and sends the message back to the unplaced queue where
      // it can be given the right application.
      await api.patch(`/emails/${it.emailId}`, { recommendationId: null });
      toast('Unlinked — it is back in "waiting to be placed".', { kind: 'success' });
      await load();
      onDone?.();
    } catch (e) {
      toast(e.body?.message || 'Could not unlink that.', { kind: 'error' });
    } finally {
      setBusy(null);
    }
  };

  if (failed) {
    return (
      <section className="space-y-2">
        <h2 className="text-sm font-medium text-brand-500">Links worth a second look</h2>
        <p className="text-xs text-slate-500">Could not check your links just now — this is not the same as finding none.</p>
      </section>
    );
  }
  if (!items || items.length === 0) return null;

  return (
    <section className="space-y-2">
      <h2 className="text-sm font-medium text-brand-500">Links worth a second look</h2>
      <p className="max-w-prose text-xs text-slate-500">
        Either the application these are filed on is never named in the message, so a rule inferred
        it, or the subject names a different role at the same company. Often the first is right — an
        Airbus mail never says Satair — but both are where a wrong link hides.
      </p>
      <ul className="space-y-1.5">
        {items.map((it) => (
          <li key={it.emailId} className="rounded-md border border-slate-800 bg-slate-950/40 px-3 py-2">
            <div className="text-xs text-slate-200">{it.subject || '(no subject)'}</div>
            <div className="mt-0.5 text-[11px] text-slate-500">
              from {it.from} · filed on{' '}
              <span className="text-slate-300">
                {it.company} — {it.title}
              </span>{' '}
              by <span className="font-mono">{it.matchMethod}</span>
            </div>
            {/* T480 — say what was seen. Asking him to diff two job titles
                himself is the work this list exists to save. */}
            {it.reason === 'role' && it.named?.length > 0 && (
              <div className="mt-0.5 text-[11px] text-amber-400">
                but the subject names{' '}
                {it.named.map((n, i) => (
                  <span key={n.id || i}>
                    {i > 0 && (i === it.named.length - 1 ? ' and ' : ', ')}
                    <span className="text-amber-300">{n.title}</span>
                    {n.status ? <span className="text-slate-500"> ({n.status})</span> : null}
                  </span>
                ))}
              </div>
            )}
            <button
              type="button"
              disabled={busy === it.emailId}
              onClick={() => unlink(it)}
              className="mt-1.5 text-[11px] text-slate-400 underline decoration-dotted transition hover:text-brand-400 disabled:opacity-50"
            >
              Not this application — unlink it
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
});

export default SuspiciousLinks;
